import React from "react";
import { Button, Card, Col, Row, Alert, Badge } from "react-bootstrap";
import { ScheduleMeeting, StartTimeEventEmit } from "react-schedule-meeting";
import "./LessonsReservationPage.css";
import hr from "date-fns/locale/hr";
import { LESSON_RESERVATION_MIN_HOURS_AHEAD, s3_url } from "common/constants";
import { BsArrowLeft, BsClock } from "react-icons/bs";
import { ISubject } from "common/types";

interface Teacher {
  name: string;
  firstName: string;
  image?: string;
  id: string;
}

interface TeacherTimeSelectionProps {
  teachers: Teacher[];
  selectedTeacher: string | null;
  availableTimeslots: {
    startTime: Date;
    endTime: Date;
    teacherName: string;
    teacherId: string;
    calendarId: number;
  }[];
  onTeacherSelect: (teacherId: string) => void;
  onTermSelect: (term: StartTimeEventEmit) => void;
  onBack: () => void;
  selectedDuration: number | null;
  selectedSubject: ISubject | null;
}

const TeacherTimeSelection: React.FC<TeacherTimeSelectionProps> = ({
  teachers,
  selectedTeacher,
  availableTimeslots,
  onTeacherSelect,
  onTermSelect,
  onBack,
  selectedDuration,
  selectedSubject,
}) => {
  const generatePossibleStartTimes = (
    slot: {
      startTime: Date;
      endTime: Date;
      teacherName: string;
      teacherId: string;
      calendarId: number;
    },
    duration: number,
  ) => {
    if (!duration) return [slot];

    const result = [];
    const slotDuration =
      (slot.endTime.getTime() - slot.startTime.getTime()) / (1000 * 60);

    const intervalMinutes = 30;
    const maxStartTimeOffset = slotDuration - duration;

    for (
      let offsetMinutes = 0;
      offsetMinutes <= maxStartTimeOffset;
      offsetMinutes += intervalMinutes
    ) {
      const newStartTime = new Date(
        slot.startTime.getTime() + offsetMinutes * 60 * 1000,
      );
      const newEndTime = new Date(
        newStartTime.getTime() + duration * 60 * 1000,
      );

      if (newEndTime <= slot.endTime) {
        result.push({
          startTime: newStartTime,
          endTime: newEndTime,
          teacherName: slot.teacherName,
          teacherId: slot.teacherId,
          calendarId: slot.calendarId,
        });
      }
    }

    return result;
  };

  const minimumAllowedStartTime = new Date(
    Date.now() + LESSON_RESERVATION_MIN_HOURS_AHEAD * 60 * 60 * 1000,
  );

  const filteredTimeslots = availableTimeslots
    .filter((slot) => !selectedTeacher || slot.teacherId === selectedTeacher)
    .flatMap((slot) => generatePossibleStartTimes(slot, selectedDuration || 0))
    .filter(
      (slot) => slot.startTime.getTime() >= minimumAllowedStartTime.getTime(),
    );

  return (
    <>
      <div className="mt-4">
        <div className="d-flex align-items-center mb-4">
          <BsArrowLeft
            className="mr-2 lessons-cursor-pointer"
            size={24}
            onClick={onBack}
          />
          <h3 className="ml-3 mb-0">Odaberite predavača</h3>
        </div>

        {selectedDuration && (
          <Card className="mb-4 bg-light">
            <Card.Body className="d-flex align-items-center flex-wrap">
              <BsClock className="me-2 text-primary" size={20} />
              <span className="fw-bold">Odabrano trajanje poduke:</span>
              <Badge bg="primary" className="ms-2 px-3 py-2">
                {selectedDuration} minuta
              </Badge>

              {selectedSubject && (
                <>
                  <span className="fw-bold ms-3">Predmet:</span>
                  <Badge bg="primary" className="ms-2 px-3 py-2">
                    {selectedSubject.name}
                  </Badge>
                </>
              )}
            </Card.Body>
          </Card>
        )}

        {teachers.length === 0 ? (
          <Alert variant="info" className="text-center">
            Nema dostupnih predavača za odabrani predmet. Molimo odaberite drugi
            predmet.
          </Alert>
        ) : (
          <Row className="justify-content-center mb-5">
            {teachers.map((teacher) => (
              <Col key={teacher.id} xs={6} sm={4} md={3} className="mb-3">
                <Card
                  className={`card-pointer h-100 teacher-card ${
                    selectedTeacher === teacher.id ? "card-selected" : ""
                  }`}
                  onClick={() => onTeacherSelect(teacher.id)}
                >
                  <div className="text-center pt-2">
                    <img
                      src={
                        teacher.image || `${s3_url}/img/lumenko/lumenko.webp`
                      }
                      alt={teacher.name}
                      className="rounded-circle teacher-image"
                      style={{
                        width: "70px",
                        height: "70px",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                  <Card.Body className="text-center p-2">
                    <Card.Title style={{ fontSize: "0.9rem" }}>
                      {teacher.firstName}
                    </Card.Title>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {!selectedTeacher ? (
          <Alert variant="info" className="text-center">
            Molimo odaberite predavača prije odabira termina
          </Alert>
        ) : filteredTimeslots.length === 0 ? (
          <Alert variant="warning" className="text-center">
            Odabrani predavač nema dostupnih termina. Molimo odaberite drugog
            predavača.
          </Alert>
        ) : (
          <>
            <h3 className="mt-4 mb-3 text-center">Odaberite slobodan termin</h3>
            <ScheduleMeeting
              availableTimeslots={filteredTimeslots}
              eventDurationInMinutes={selectedDuration || 30}
              onStartTimeSelect={onTermSelect}
              locale={hr}
              // Language props
              lang_cancelButtonText="Odustani"
              lang_confirmButtonText="Potvrdi"
              lang_emptyListText="Nema slobodnih termina"
              lang_goToNextAvailableDayText="Sljedeći slobodan"
              lang_noFutureTimesText="Nema termina u budućnosti"
              lang_selectedButtonText="Odabrano:"
              // Date format props
              format_nextFutureStartTimeAvailableFormatString="cccc, LLLL do"
              format_selectedDateDayTitleFormatString="cccc, LLLL do"
              format_selectedDateMonthTitleFormatString="LLLL yyyy"
              format_startTimeFormatString="H:mm"
            />
          </>
        )}
      </div>
    </>
  );
};

export default TeacherTimeSelection;
